/**
 * CaptionPage — 字幕専用画面 (/caption)
 *
 * - 操作UIを一切表示しない
 * - WebSocketから字幕を受信して表示
 * - OBSブラウザソース対応（URLパラメータでスタイル指定可能）
 *   ?style=white-black | transparent-black | transparent-white
 *   ?size=64 （フォントサイズ px）
 *   ?original=1 （原文も表示）
 *   ?session=default
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionStore } from '../stores/sessionStore';
import { useWorkerWS } from '../hooks/useWorkerWS';
import CaptionDisplay from '../components/CaptionDisplay';
import RollupCaption from '../components/RollupCaption';
import type { ServerMessage, OBSStyleMode, CaptionState } from '../types';

const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? 'http://localhost:8787';

export default function CaptionPage() {
  const [searchParams] = useSearchParams();
  const store = useSessionStore();
  const [lastUpdate, setLastUpdate] = useState(0); // 再レンダリング強制用

  // URLパラメータ（固定値）— 指定されていればWSからの更新を無視
  const urlSize = searchParams.get('size');
  const urlStyle = searchParams.get('style') as OBSStyleMode | null;
  const urlOriginal = searchParams.get('original');
  const urlBg = searchParams.get('bg'); // 'black' でRoland DSKルミナンスキー用黒背景
  const eventPub = searchParams.get('event');
  const sessionId = searchParams.get('session') ?? (eventPub ? `event-${eventPub}` : 'default');
  // イベント固定URL：主催者が入り直して新しいセッションになったら、自動でつなぎ直す
  useEffect(() => {
    if (!eventPub) return;
    let first: string | null | undefined;
    const check = () => fetch(`${WORKER_URL}/live?event=${eventPub}`)
      .then((r) => r.json())
      .then((d: { sessionId?: string | null }) => {
        const sid = d.sessionId ?? null;
        if (first === undefined) { first = sid; return; }
        if (sid !== first) window.location.reload();
      })
      .catch(() => {});
    void check();
    const timer = setInterval(check, 10000);
    return () => clearInterval(timer);
  }, [eventPub]);

  // 字幕位置は上固定
  const captionPosition: 'top' = 'top';
  const [fontSize, setFontSize] = useState<number>(urlSize ? Number(urlSize) : 54);
  const [obsStyle, setObsStyle] = useState<OBSStyleMode>(urlStyle ?? 'transparent-white');
  const [showOriginal, setShowOriginal] = useState<boolean>(urlOriginal === '1');
  // 表示する言語：
  //   なし      → 1つ目の言語だけ（会場スクリーン用・従来どおり）
  //   lang=both → 2つの言語の訳を1段にまとめて表示（Zoom会議の画面共有用）
  //   lang=ja 等 → その言語の訳だけ（QRからスマホで見る用）
  const langFilter = searchParams.get('lang');
  // スマホ（QRで言語を選んだ画面）：字幕を流れる文章として表示する（切らずに全文、過去分もスクロールで読める）
  // セミナー用の即時表示（順番待ちしない・最大2行）。言語指定のない字幕画面の標準。
  // 従来の順番待ち表示（キュー方式）は queue=1 を付けたときだけ使う
  const live = langFilter !== 'both'
    && (searchParams.get('live') === '1' || (!langFilter && searchParams.get('queue') !== '1'));
  const phoneMode = Boolean(langFilter) && langFilter !== 'both' && !live;
  const [phoneLines, setPhoneLines] = useState<string[]>([]);
  const [phoneInterim, setPhoneInterim] = useState('');

  // ── 会議用（lang=both）：下段に出す訳を選ぶ ──
  // 翻訳AIは、話している言語と同じ言語に「言い直し」を返すことがある（例：日本語の発言に日本語）。
  // 原文（上段）が届いたら、原文と違う文字種の訳だけを下段に出す。
  type LaneCap = { cap: CaptionState; startAt: number; at: number };
  const laneRef = useRef<Record<number, LaneCap | null>>({ 1: null, 2: null });
  const topRef = useRef<{ text: string; at: number; final: boolean } | null>(null);
  // 会議用の流れる字幕：上段＝英語の訳、下段＝日本語の訳（layout=original で従来の原文＋訳）
  const rollup = langFilter === 'both' && searchParams.get('layout') !== 'original';
  // at＝表示を最後に更新した時刻、tr＝訳（レーン1・2）が最後に届いた時刻
  const rollRef = useRef<{ finals: string[]; interim: string; at: number; tr: number }[]>([{ finals: [], interim: '', at: 0, tr: 0 }, { finals: [], interim: '', at: 0, tr: 0 }]);
  // 確認用：test_stall=1 のときは英語の訳を無視して、上段が止まった状態を再現する
  const testStall = searchParams.get('test_stall') === '1';
  // 無音が30秒続いたら消すため、5秒ごとに描き直す
  useEffect(() => {
    if (!rollup) return;
    const t = setInterval(() => setLastUpdate(Date.now()), 5000);
    return () => clearInterval(t);
  }, [rollup]);
  const scriptOf = (t: string) => (/[\uAC00-\uD7AF]/.test(t) ? 'ko' : /[\u3040-\u30FF\u4E00-\u9FFF]/.test(t) ? 'cjk' : /[\u0E00-\u0E7F]/.test(t) ? 'th' : 'latin');
  // 言語コード → 文字の種類（訳の言語が、話している言語と同じかを見分けるため）
  const scriptOfLang = (code?: string) => (code === 'ja' || code === 'zh' ? 'cjk' : code === 'ko' ? 'ko' : code === 'th' ? 'th' : 'latin');
  // いま話している言語の文字の種類（原文の文字起こしから判定）。分かるまでは null
  const spokenRef = useRef<string | null>(null);
  // live=1 用：翻訳AIが返す原文（リアルタイム）から判定した、いま話している言語。原文の文字起こしより速い
  const liveSpokenRef = useRef<string | null>(null);
  const recentScriptsRef = useRef<string[]>([]);
  const pickBottom = () => {
    const cands = [laneRef.current[1], laneRef.current[2]].filter(Boolean) as LaneCap[];
    if (cands.length === 0) return;
    cands.sort((a, b) => b.at - a.at);
    let pick: LaneCap | undefined = cands[0];
    const src = liveSpokenRef.current || spokenRef.current;
    if (src) {
      // 話している言語と違う言語の訳だけを出す（同じ言語への「言い直し」は出さない）
      pick = cands.find((c) => scriptOfLang(c.cap.target) !== src);
    }
    if (pick && store.currentCaption !== pick.cap) store.setCurrentCaption(pick.cap);
  };

  // URLパラメータが指定されている場合はWS更新を無視
  const lockedRef = useRef({ size: urlSize !== null, style: urlStyle !== null, original: urlOriginal !== null });

  const handleMessage = useCallback(
    (msg: ServerMessage) => {
      switch (msg.type) {
        case 'caption':
          if (msg.lane === 3) {
            // 原文から、いま話している言語を判定（文字が十分あるときだけ更新）
            // 確定した原文だけを使い、直近3つの多数決で決める（1回の誤判定でちらつかないように）
            if (!msg.interim && (msg.translation ?? '').replace(/[\s\p{P}]/gu, '').length >= 3) {
              const hist = [...recentScriptsRef.current, scriptOf(msg.translation)].slice(-3);
              recentScriptsRef.current = hist;
              const count = (k: string) => hist.filter((x) => x === k).length;
              const latest = hist[hist.length - 1];
              const best = hist.reduce((b, k) => (count(k) > count(b) ? k : b), latest);
              spokenRef.current = best;
            }
            {
              // 多数決を待たず、原文の文字起こし（途中経過を含む）1件で話している言語を切り替える（全表示共通）
              const t3l = (msg.translation ?? '').replace(/[\s\p{P}]/gu, '');
              if (t3l.length >= 3) liveSpokenRef.current = scriptOf(t3l.slice(-12));
              if (live && !langFilter) { pickBottom(); setLastUpdate(Date.now()); }
            }
            // 原文（文字起こし）：会議用字幕のときだけ上段に出す
            if (langFilter === 'both') {
              store.setCurrentCaption2({ interim: msg.interim, original: '', translation: msg.translation, direction: msg.direction, target: msg.target });
              const t0 = topRef.current;
              const newUtt = !t0 || t0.final;
              topRef.current = { text: msg.translation, at: newUtt ? Date.now() : t0!.at, final: !msg.interim };
              // 英語で話しているときは、上段（英語）に文字起こしをそのまま出す。
              // （英語→英語の「訳」は翻訳エンジンが返さないことが多いため使わない）
              const t3 = (msg.translation ?? '').trim();
              const r0 = rollRef.current[0];
              if (rollup && t3 && scriptOf(t3) === 'latin') {
                if (msg.interim) r0.interim = t3;
                else { r0.interim = ''; r0.finals = [...r0.finals, t3].slice(-8); }
                r0.at = Date.now();
              }
              pickBottom();
              setLastUpdate(Date.now());
            }
            break;
          }
          if (live) {
            const lane = msg.lane === 2 ? 2 : 1;
            const cap = { interim: msg.interim, original: msg.original ?? '', translation: msg.translation, direction: msg.direction, target: msg.target };
            if (lane === 1) {
              // 原文の末尾（直近の発話）の文字の種類で、話している言語をすぐ判定する
              const tail = (msg.original ?? '').slice(-12).replace(/[\s\p{P}]/gu, '');
              if (tail.length >= 3) liveSpokenRef.current = scriptOf(tail);
            }
            if (!langFilter) {
              // 自動：話している言語ではない方の訳を出す（片方向なら1つ目の言語）
              const now = Date.now();
              laneRef.current[lane] = { cap, startAt: now, at: now };
              pickBottom();
            } else {
              // 言語指定：その言語の訳だけ。話している言語と同じ言語への言い直しは出さない
              const tgt = msg.target ?? (msg.direction === 'en-ja' ? 'ja' : 'en');
              if (tgt !== langFilter) break;
              const spk = liveSpokenRef.current || spokenRef.current;
              if (spk && scriptOfLang(langFilter) === spk) break;
              store.setCurrentCaption(cap);
            }
            setLastUpdate(Date.now());
            break;
          }
          if (rollup) {
            const tgt = msg.target ?? (msg.direction === 'en-ja' ? 'ja' : 'en');
            const row = tgt === 'en' ? 0 : tgt === 'ja' ? 1 : (msg.lane === 2 ? 1 : 0);
            if (testStall && row === 0) break;
            // 英語で話している間の英語の「訳」（言い直し）は出さない。上段は文字起こしを使う
            if (row === 0 && (liveSpokenRef.current || spokenRef.current) === 'latin') break;
            const r = rollRef.current[row];
            const t = (msg.translation ?? '').trim();
            r.tr = Date.now();
            if (msg.interim) r.interim = t;
            else { r.interim = ''; if (t) r.finals = [...r.finals, t].slice(-8); }
            r.at = Date.now();
            setLastUpdate(r.at);
            break;
          }
          if (langFilter === 'both') {
            const lane = msg.lane === 2 ? 2 : 1;
            const prev = laneRef.current[lane];
            const now = Date.now();
            const startAt = prev && prev.cap.interim ? prev.startAt : now;
            laneRef.current[lane] = {
              cap: { interim: msg.interim, original: '', translation: msg.translation, direction: msg.direction, target: msg.target },
              startAt, at: now,
            };
            pickBottom();
            setLastUpdate(now);
            break;
          }
          if (langFilter === 'both') {
            // どちらの言語の訳も表示する
          } else if (langFilter) {
            if (msg.target !== langFilter) break;
            // 話している言語と同じ言語の画面には、AIの「言い直し」を出さない
            if ((liveSpokenRef.current || spokenRef.current) && scriptOfLang(langFilter) === (liveSpokenRef.current || spokenRef.current)) {
              // 話す言語が切り替わった直後は判定が遅れるので、確定した訳は少し待ってから判断し直す
              if (!msg.interim) {
                const done = msg.translation.trim();
                setTimeout(() => {
                  if (done && (liveSpokenRef.current || spokenRef.current) !== scriptOfLang(langFilter)) setPhoneLines((ls) => [...ls, done].slice(-10));
                }, 1500);
              }
              break;
            }
            if (msg.interim) {
              setPhoneInterim(msg.translation);
            } else {
              const done = msg.translation.trim();
              setPhoneInterim('');
              if (done) setPhoneLines((ls) => [...ls, done].slice(-10));
            }
            break;
          } else if (msg.lane === 2) {
            break;
          }
          store.setCurrentCaption({
            interim: msg.interim,
            original: msg.original,
            translation: msg.translation,
            direction: msg.direction,
            target: msg.target,
          });
          setLastUpdate(Date.now());
          break;

        case 'direction':
          store.setDirection(msg.value);
          break;



        case 'clear':
        case 'stopped':
          store.clearCaption();
          break;

        case 'fontSize':
          if (!lockedRef.current.size) setFontSize(msg.value);
          break;

        case 'obsStyle':
          if (!lockedRef.current.style) setObsStyle(msg.value as OBSStyleMode);
          break;

        case 'showOriginal':
          if (!lockedRef.current.original) setShowOriginal(msg.value);
          break;

        case 'status':
          store.setConnectionStatus(msg.connected ? 'connected' : 'disconnected');
          if (msg.fontSize && !lockedRef.current.size) setFontSize(msg.fontSize);
          if (msg.obsStyle && !lockedRef.current.style) setObsStyle(msg.obsStyle as OBSStyleMode);
          if (msg.showOriginal !== undefined && !lockedRef.current.original) setShowOriginal(msg.showOriginal);
          break;
      }
    },
    [store, live, langFilter, rollup, testStall]
  );

  useWorkerWS({
    url: WORKER_URL,
    role: 'caption',
    sessionId,
    onMessage: handleMessage,
    autoConnect: true,
  });

  // OBSスタイルに応じてbody背景を設定
  useEffect(() => {
    const isTransparent = obsStyle !== 'white-black';
    // ?bg=black → Roland DSKルミナンスキー用（黒背景で白文字だけ残す）
    const bg = urlBg === 'black' ? 'black' : isTransparent ? 'transparent' : 'white';
    document.body.style.background = bg;
    document.documentElement.style.background = bg;

    // key-modeクラス: 半透明背景・シャドウを除去して純白文字にする
    if (urlBg === 'black') {
      document.body.classList.add('key-mode');
    } else {
      document.body.classList.remove('key-mode');
    }

    return () => {
      document.body.style.background = '';
      document.documentElement.style.background = '';
      document.body.classList.remove('key-mode');
    };
  }, [obsStyle, urlBg]);

  // スリープ防止（Chromeではページが表示されている間は有効）
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch {
        // WakeLock非対応環境では無視
      }
    };

    requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release().catch(() => {});
    };
  }, []);

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        position: 'fixed',
        inset: 0,
      }}
    >
      {phoneMode ? (
        (() => {
          // 直前の訳2つを小さく灰色で、いま話している訳を大きく白で表示（溜まらないよう最新の3つだけ）
          const current = phoneInterim || phoneLines[phoneLines.length - 1] || '';
          const prev = (phoneInterim ? phoneLines.slice(-2) : phoneLines.slice(-3, -1));
          return (
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#000', color: '#fff', padding: '1.5rem 1.25rem', fontFamily: 'system-ui, -apple-system, sans-serif', display: 'flex', flexDirection: 'column' }}>
              <div style={{ minHeight: '28%' }}>
                {prev.map((l, i) => (
                  <p key={phoneLines.length + i} style={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.45, margin: '0 0 0.75rem', color: i === prev.length - 1 ? '#9ca3af' : '#6b7280' }}>{l}</p>
                ))}
              </div>
              <div style={{ borderTop: '1px solid #374151', paddingTop: '1rem' }}>
                <p style={{ fontSize: '1.7rem', fontWeight: 700, lineHeight: 1.45, margin: 0, color: current ? '#fff' : '#6b7280' }}>{current || '…'}</p>
              </div>
            </div>
          );
        })()
      ) : rollup ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          {[0, 1].map((i) => {
            const r = rollRef.current[i];
            const stale = r.at && Date.now() - r.at > 30000;
            return (
              <div key={i} style={{ flex: 1, minHeight: 0, borderTop: i === 1 ? '1px solid rgba(255,255,255,0.15)' : undefined }}>
                <RollupCaption finals={stale ? [] : r.finals} interim={stale ? '' : r.interim} fontSize={fontSize} lines={3} obsStyle={obsStyle} />
              </div>
            );
          })}
        </div>
      ) : langFilter === 'both' ? (
        // 会議用：上段＝原文、下段＝訳
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
            <CaptionDisplay caption={store.currentCaption2} fontSize={Math.round(fontSize * 0.8)} showOriginal={false} obsStyle={obsStyle} captionPosition={captionPosition} live />
          </div>
          <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
            <CaptionDisplay caption={store.currentCaption} fontSize={fontSize} showOriginal={false} obsStyle={obsStyle} captionPosition={captionPosition} live />
          </div>
        </div>
      ) : (
        <CaptionDisplay
          caption={store.currentCaption}
          fontSize={langFilter && !live ? Math.min(fontSize, Math.max(22, Math.round(window.innerWidth / 11))) : fontSize}
          showOriginal={showOriginal}
          obsStyle={obsStyle}
          captionPosition={captionPosition}
          live={Boolean(langFilter) || live}
          liveLines={live ? 2 : 4}
        />
      )}
    </div>
  );
}
